import React, { useEffect, useState } from 'react';
import { Box, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { ITableHeadings, ITableMetadata } from '../../../common/interfaces/common.interface';
import BasicTable from '../../../components/BasicTable/BasicTable';
import { initialMetadataState, MESSAGE_TYPE } from '../../../common/constants/common.constants';
import TableHeader from '../../../components/TableHeader';
import { TeacherDto } from '../../../common/interfaces/kindergarten.interface';
import { ReactComponent as AddIcon } from '../../../assets/add-icon.svg';
import { fetchTeachers, getGroupsWithLocationName, updateTeacherStatus } from '../../../api/kindergarten.api';
import { createNotification } from '../../../utilities/helper/notification.helper';
import GenericStatusSwitch from '../../../components/GenericStatusSwitch';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import { useNavigate } from 'react-router-dom';
import GenericMultiSelectWithSearch from '../../../components/GenericMultiSelectWithSearch';
import { ReactComponent as EditIcon } from '../../../assets/edit-icon.svg';
import { ReactComponent as FaceIcon } from '../../../assets/face-icon.svg';
import { GeneralStatusEnum, ImportUserType, SortOrderEnum } from '../../../common/enums/common.enum';
import GenericInfoModal from '../../../components/GenericInfoModal';
import { TeachersTabSortKeyEnum } from '../../../common/enums/kindergarten.enum';
import GenericImportButton from '../../../components/GenericImportButton';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../theme';
export const TEACHERS_TABLE_HEADINGS: ITableHeadings[] = [
  { id: 1, value: '', label: 'common.name', sortable: true, sortKey: TeachersTabSortKeyEnum.NAME, width: '20%' },
  { id: 2, value: '', label: 'common.email', sortable: true, sortKey: TeachersTabSortKeyEnum.EMAIL },
  { id: 3, value: '', label: 'common.phone', sortable: true, sortKey: TeachersTabSortKeyEnum.PHONE },
  { id: 4, value: '', label: 'common.groups', sortable: true, sortKey: TeachersTabSortKeyEnum.GROUPS, width: '40%' },
  { id: 5, value: '', label: 'common.status', sortable: true, sortKey: TeachersTabSortKeyEnum.STATUS },
  { id: 6, value: '', label: '', sortable: false }
];

const TeachersTab: React.FC<{ kindergartenId: string; kindergartenName: string }> = ({ kindergartenId, kindergartenName }) => {
  const { t } = useTranslation();
  const [teachers, setTeachers] = useState<TeacherDto[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<ITableMetadata>(initialMetadataState);
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useState<string>('');
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<TeachersTabSortKeyEnum>();
  const [sortOrder, setSortOrder] = useState<SortOrderEnum>();
  const [teacherToDeactivate, setTeacherToDeactivate] = useState<TeacherDto | null>(null);

  useEffect(() => {
    loadGroups();
  }, [kindergartenId]);

  useEffect(() => {
    loadTeachers();
  }, [kindergartenId, metadata.currentPage, metadata.itemsPerPage, searchParams, selectedGroupIds, sortBy, sortOrder]);

  const handleSearchChange = (newSearch: string) => {
    setMetadata(prev => ({ ...prev, currentPage: 1 }));
    setSearchParams(newSearch);
  };

  const handleSortChange = (sortKey: string | null, order: 'asc' | 'desc' | null) => {
    setSortBy(sortKey ? (sortKey as TeachersTabSortKeyEnum) : undefined);
    setSortOrder(order ? (order as SortOrderEnum) : undefined);
  };

  const loadTeachers = async () => {
    setLoader(true);
    try {
      const result = await fetchTeachers(
        kindergartenId,
        metadata.currentPage,
        metadata.itemsPerPage,
        sortBy,
        sortOrder,
        searchParams,
        selectedGroupIds
      );
      setTeachers(result.results ?? []);
      if (result.paginationInfo) {
        setMetadata(prev => ({
          ...prev,
          totalItems: result.paginationInfo?.totalItems ?? 0,
          totalPages: result.paginationInfo?.totalPages ?? 0
        }));
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('teachers.loadFailed'));
    } finally {
      setLoader(false);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await getGroupsWithLocationName(kindergartenId);
      if (response?.results) {
        setGroups(response.results);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('groups.listLoadFailed'));
    }
  };

  const handleChangeTeacherStatus = async (teacherId: string) => {
    if (!kindergartenId) return;

    try {
      await updateTeacherStatus(kindergartenId, teacherId);
      createNotification(MESSAGE_TYPE.SUCCESS, t('teachers.statusChanged'));
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('teachers.statusChangeFailed'));
    }
  };

  const handleConfirmDeactivation = async () => {
    if (!teacherToDeactivate) return;
    handleChangeTeacherStatus(teacherToDeactivate.id);
    setTeachers(prev =>
      prev.map(t => (t.id === teacherToDeactivate.id ? { ...t, status: GeneralStatusEnum.INACTIVE, groups: undefined } : t))
    );
    setTeacherToDeactivate(null);
  };

  const handleCreateClick = () => {
    navigate(`/${kindergartenId}/teacher/create/${encodeURIComponent(kindergartenName)}`);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: 24,
            color: 'text.primary',
            fontFamily: 'Poppins'
          }}
        >
          {t('kindergarten.tabs.teachers')}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <GenericImportButton
            label={t('common.import')}
            type={ImportUserType.TEACHER}
            kindergartenId={kindergartenId}
            onSuccessImport={loadTeachers}
          />
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            label={t('common.create')}
          />
        </Stack>
      </Stack>
      <Box
        sx={{
          border: `1px solid ${palette.line}`,
          borderRadius: 4
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}
        >
          <TableHeader searchTitle={t('common.searchByName')} sortOptions={[]} setSearchValue={handleSearchChange} />
          <GenericMultiSelectWithSearch
            label={t('common.group')}
            options={groups}
            onClearAll={async () => {
              setSelectedGroupIds([]);
            }}
            onShowResults={async ids => {
              setSelectedGroupIds(ids);
              setMetadata(prev => ({ ...prev, currentPage: 1 }));
            }}
          />
        </Box>
        <BasicTable
          headings={TEACHERS_TABLE_HEADINGS}
          loader={loader}
          metadata={metadata}
          setMetadata={setMetadata}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
        >
          {teachers.map(teacher => (
            <TableRow
              key={teacher.id}
              hover
              sx={{ cursor: ' pointer' }}
              onClick={() => navigate(`/${kindergartenId}/teacher/${teacher.id}/details/${encodeURIComponent(kindergartenName)}`)}
            >
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  {teacher.profileImage ? (
                    <img
                      src={teacher.profileImage}
                      alt={teacher.fullName}
                      style={{ color: 'text.primary', width: 34, height: 34, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    <FaceIcon style={{ width: 34, height: 34 }} />
                  )}
                  <Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 400 }}>
                    {teacher.fullName}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>{teacher.email}</TableCell>
              <TableCell>{teacher.phoneNumber || '-'}</TableCell>
              <TableCell>{teacher.groups?.map((g: any) => g.name).join(', ') ?? '-'}</TableCell>
              <TableCell>
                <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                  <GenericStatusSwitch
                    status={teacher.status}
                    onChange={() => {
                      if (teacher.status === GeneralStatusEnum.ACTIVE) {
                        setTeacherToDeactivate(teacher);
                      } else {
                        handleChangeTeacherStatus(teacher.id);
                        setTeachers(prev =>
                          prev?.map(t =>
                            t.id === teacher.id
                              ? {
                                  ...t,
                                  status: t.status === GeneralStatusEnum.ACTIVE ? GeneralStatusEnum.INACTIVE : GeneralStatusEnum.ACTIVE
                                }
                              : t
                          )
                        );
                      }
                    }}
                    showStatusText={false}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <EditIcon
                  style={{ cursor: 'pointer' }}
                  onClick={e => {
                    e.stopPropagation();
                    navigate(`/${kindergartenId}/teacher/${teacher.id}/edit/${encodeURIComponent(kindergartenName)}`);
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
          {teacherToDeactivate && (
            <GenericInfoModal
              open={true}
              onClose={() => setTeacherToDeactivate(null)}
              onConfirm={handleConfirmDeactivation}
              label={t('teachers.deactivate')}
              message={t('common.confirmDeactivate')}
              confirmLabel={t('common.deactivate')}
              entityName={teacherToDeactivate.fullName}
            />
          )}
        </BasicTable>
      </Box>
    </Box>
  );
};
export default TeachersTab;
